/**
 * 字牌（跑胡子）判定裁决引擎
 * 严格按照白皮书优先级裁决：提/跑 > 胡 > 碰/扫 > 吃 > 过
 */
import { Player, Card, AvailableActions, WinResult } from './types';
import { Rules } from './Rules';
import { DEFAULT_RULE_CONFIG, RuleConfig } from './rules.config';

export class JudgeEngine {
  /**
   * 计算指定玩家对当前判定区牌的可用操作
   * @param player 目标玩家
   * @param targetCard 判定区的牌 (摸出或打出)
   * @param isDrawn 是否为摸出来的牌 (true=摸牌, false=出牌)
   * @param isActor 是否为当前摸牌者或打牌者本人
   * @param isNextPlayer 是否为当前操作者的下家
   * @param minHuxi 起胡胡息
   */
  public static evaluatePlayerActions(
    player: Player,
    targetCard: Card | null,
    isDrawn: boolean,
    isActor: boolean,
    isNextPlayer: boolean,
    minHuxi: number = Rules.DEFAULT_MIN_HUXI,
    ruleConfig: RuleConfig = DEFAULT_RULE_CONFIG
  ): AvailableActions {
    const result: AvailableActions = {
      canHu: false,
      canTi: false,
      canPao: false,
      canSao: false,
      canPeng: false,
      canChi: false,
      chiOptions: [],
      canPass: true
    };

    if (!targetCard) {
      return result;
    }

    // 1. 提 (Priority 1) - 摸牌者手中有 3 张相同牌
    if (isDrawn && isActor) {
      if (Rules.canTi(player.hand, targetCard)) {
        result.canTi = true;
      }
    }

    // 2. 扫 (Priority 1) - 摸牌者手中有 2 张相同牌
    if (isDrawn && isActor) {
      if (Rules.canSao(player.hand, targetCard)) {
        result.canSao = true;
      }
    }

    // 3. 跑 (Priority 1) - 已有碰/扫面子，或手中有坎 (注：碰牌只能跑摸出的牌)
    if (Rules.canPao(player.hand, player.melds, targetCard, isDrawn, isActor)) {
      result.canPao = true;
    }

    // 4. 胡牌 (Priority 2) - 任何人均可抢胡，需满足 >= minHuxi
    const winResult = Rules.calculateWin(player.hand, player.melds, targetCard, isActor && isDrawn, minHuxi, ruleConfig)
      || Rules.calculatePengWin(player.hand, player.melds, targetCard, minHuxi, ruleConfig);
    if (winResult && winResult.isWin) {
      result.canHu = true;
    }

    // 5. 碰 (Priority 3) - 非摸/出牌者本人，且手中有 2 张相同
    if (!isActor) {
      if (Rules.canPeng(player.hand, targetCard)) {
        result.canPeng = true;
      }
    }

    // 6. 吃 (Priority 4) - 仅限摸牌者本人 (如果是自己摸的无人胡碰) 或 下家 (且必须非本人打出的牌)
    if (!isActor && isNextPlayer) {
      const options = Rules.getChiOptions(player.hand, targetCard, ruleConfig);
      if (options.length > 0) {
        result.canChi = true;
        result.chiOptions = options;
      }
    } else if (isDrawn && isActor) {
      // 摸牌者自己也可以吃自己摸出的牌 (如果没有扫/提且无人抢胡)
      const options = Rules.getChiOptions(player.hand, targetCard, ruleConfig);
      if (options.length > 0) {
        result.canChi = true;
        result.chiOptions = options;
      }
    }

    return result;
  }

  /**
   * 判定全局所有玩家是否有抢胡 (按座位顺位优先级排序)
   */
  public static findWinningPlayers(
    players: Player[],
    turnIndex: number,
    targetCard: Card,
    isDrawn: boolean,
    minHuxi: number = Rules.DEFAULT_MIN_HUXI,
    ruleConfig: RuleConfig = DEFAULT_RULE_CONFIG
  ): { player: Player; winResult: WinResult }[] {
    const winners: { player: Player; winResult: WinResult }[] = [];

    // 按摸牌者开始的座次顺序轮询 (摸牌者 -> 下家 -> 闲家)
    for (let i = 0; i < players.length; i++) {
      const pIdx = (turnIndex + i) % players.length;
      const player = players[pIdx];
      const isSelf = pIdx === turnIndex && isDrawn;

      const winRes = Rules.calculateWin(player.hand, player.melds, targetCard, isSelf, minHuxi, ruleConfig)
        || Rules.calculatePengWin(player.hand, player.melds, targetCard, minHuxi, ruleConfig);
      if (winRes && winRes.isWin) {
        winners.push({ player, winResult: winRes });
      }
    }

    return winners;
  }
}

